/**
 * Sibling Swatches - Responsive visibility handler
 * Handles expand/collapse functionality for both product cards and product pages
 * Supports auto mode (fit to one line) and manual mode (set number)
 * Never shows +1 - if only 1 hidden, shows one less swatch so expand shows +2
 */
(function() {
  const GAP = 4; // Gap between swatches in pixels

  function initSwatchContainer(container, isProductCard) {
    const list = container.querySelector(isProductCard ? '.pcard-sibling-swatches__list' : '.product-sibling-swatches__list');
    if (!list) return;

    const swatches = Array.from(list.querySelectorAll(isProductCard ? '.pcard-sibling-swatches__item' : '.product-sibling-swatches__item'));
    const toggleBtn = list.querySelector(isProductCard ? '.pcard-sibling-swatches__toggle' : '.product-sibling-swatches__toggle');
    const toggleCount = toggleBtn?.querySelector(isProductCard ? '.pcard-sibling-swatches__toggle-count' : '.product-sibling-swatches__toggle-count');

    if (!swatches.length || !toggleBtn) return;

    // Get settings from data attributes
    const swatchMode = container.dataset.swatchMode || 'auto';
    const manualLimit = parseInt(container.dataset.swatchLimit) || (isProductCard ? 4 : 6);

    // Get swatch size from CSS variable
    const computedStyle = getComputedStyle(container);
    const swatchSize = parseInt(computedStyle.getPropertyValue('--swatch-size')) || (isProductCard ? 32 : 64);
    const buttonSize = swatchSize;

    let isExpanded = false;

    function calculateAutoLimit() {
      const containerWidth = list.offsetWidth;
      // Available width = container - button - gap for button
      const availableWidth = containerWidth - buttonSize - GAP;
      // How many swatches fit in one line
      const swatchesPerRow = Math.floor((availableWidth + GAP) / (swatchSize + GAP));
      return Math.max(1, swatchesPerRow);
    }

    function updateSwatchVisibility() {
      // Calculate visible count based on mode
      let visibleCount;
      if (swatchMode === 'auto') {
        visibleCount = calculateAutoLimit();
      } else {
        visibleCount = manualLimit;
      }

      // Check if we would have only 1 hidden - if so, show one less to make it +2
      let hiddenCount = swatches.length - visibleCount;
      if (hiddenCount === 1 && visibleCount > 1) {
        visibleCount -= 1;
        hiddenCount = 2;
      }

      if (isExpanded) {
        // Show all swatches
        swatches.forEach(swatch => swatch.classList.remove('swatch-hidden'));
        toggleBtn.dataset.state = 'collapse';
        if (toggleCount) toggleCount.textContent = '';
        // Only show toggle if there are hidden swatches when collapsed
        toggleBtn.style.display = (swatches.length > visibleCount) ? 'inline-flex' : 'none';
      } else {
        // Show limited swatches
        swatches.forEach((swatch, index) => {
          if (index < visibleCount) {
            swatch.classList.remove('swatch-hidden');
          } else {
            swatch.classList.add('swatch-hidden');
          }
        });

        if (hiddenCount > 0) {
          toggleBtn.style.display = 'inline-flex';
          toggleBtn.dataset.state = 'expand';
          if (toggleCount) toggleCount.textContent = hiddenCount;
        } else {
          toggleBtn.style.display = 'none';
        }
      }
    }

    toggleBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      isExpanded = !isExpanded;
      updateSwatchVisibility();
    });

    // Initial update
    updateSwatchVisibility();

    // Store update function for resize handler
    container._updateSwatches = updateSwatchVisibility;
    container._resetExpanded = function() {
      isExpanded = false;
      updateSwatchVisibility();
    };
  }

  function initAllSwatches() {
    // Product card swatches
    document.querySelectorAll('.pcard-sibling-swatches').forEach(container => {
      if (!container._initialized) {
        initSwatchContainer(container, true);
        container._initialized = true;
      }
    });

    // Product page swatches
    document.querySelectorAll('.product-sibling-swatches').forEach(container => {
      if (!container._initialized) {
        initSwatchContainer(container, false);
        container._initialized = true;
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSwatches);
  } else {
    initAllSwatches();
  }

  // Debounced resize handler
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      document.querySelectorAll('.pcard-sibling-swatches, .product-sibling-swatches').forEach(container => {
        if (container._updateSwatches) {
          container._updateSwatches();
        }
      });
    }, 100);
  });

  // MutationObserver for dynamically added swatches
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) {
          if (node.classList?.contains('pcard-sibling-swatches') && !node._initialized) {
            initSwatchContainer(node, true);
            node._initialized = true;
          } else if (node.classList?.contains('product-sibling-swatches') && !node._initialized) {
            initSwatchContainer(node, false);
            node._initialized = true;
          }
          // Check children
          node.querySelectorAll?.('.pcard-sibling-swatches').forEach(c => {
            if (!c._initialized) {
              initSwatchContainer(c, true);
              c._initialized = true;
            }
          });
          node.querySelectorAll?.('.product-sibling-swatches').forEach(c => {
            if (!c._initialized) {
              initSwatchContainer(c, false);
              c._initialized = true;
            }
          });
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
